import React, { Fragment, useState, useEffect } from "react";
import {
  Card,
  Table,
  Col,
  Spinner,
  Form,
  Button,
  Badge,
  Pagination,
  Image,
} from "react-bootstrap";
import { dateHandler } from "../../utils/ComponentUtilse";
import * as XLSX from "xlsx";
import { useGetComplainceReport } from "../../actions/dashboard-actions";
import { Link } from "react-router-dom";

function ComplianceCheck() {
  const [originalData, setOriginalData] = useState([]);
  const [displayData, setDisplayData] = useState([]);
  const [isLoading, setIsLoading] = useState(true); // Initialize as true for page loader
  const [sortConfig, setSortConfig] = useState({ key: null, direction: "asc" });
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;

  const [filters, setFilters] = useState({
    is_british: "",
    is_sponsored: "",
    rtw_status: "",
    visa_status: "",
  });

  const { data: complianceData, isLoading: loadingComplianceData } =
    useGetComplainceReport();

  // Page loader effect
  useEffect(() => {
    if (!loadingComplianceData) {
      setIsLoading(false);
    }
  }, [loadingComplianceData]);

  const getAvatarContent = (employee) => {
    if (employee.profile_image_url && employee.profile_image_url !== "NA") {
      return (
        <Image
          src={employee.profile_image_url}
          roundedCircle
          width="40"
          height="40"
          className="border"
          onError={(e) => {
            e.target.onerror = null;
            e.target.src = "";
          }}
        />
      );
    }
    const name = employee.employee_fullname || "";
    const initials = name
      .split(" ")
      .map((part) => part.charAt(0).toUpperCase())
      .join("")
      .substring(0, 2);

    return (
      <div
        className="d-flex align-items-center justify-content-center rounded-circle bg-primary text-white"
        style={{ width: "40px", height: "40px" }}
      >
        {initials}
      </div>
    );
  };

  const getPassportStatus = (employee) => {
    if (!employee.passport?.number || employee.passport.number === "NA") {
      return "Need to Upload";
    }

    const hasAttachment =
      employee.passport?.attachment_url &&
      employee.passport.attachment_url !== "NA";

    return (
      <div className="d-flex align-items-center">
        <span>{employee.passport.number}</span>
        {hasAttachment && (
          <a
            href={employee.passport.attachment_url}
            target="_blank"
            rel="noopener noreferrer"
            download
            className="ms-2"
          >
            <i className="fas fa-download" />
          </a>
        )}
      </div>
    );
  };

  const getVisaStatus = (employee) => {
    if (employee.is_british_employee) {
      return "Not Required";
    }

    if (!employee.visa?.type || employee.visa.type === "NA") {
      return "Need to Upload";
    }

    const hasAttachment =
      employee.visa?.attachment_url && employee.visa.attachment_url !== "NA";

    const visaInfo = [];
    if (employee.visa.type) visaInfo.push(`Type: ${employee.visa.type}`);
    if (employee.visa.issue_date) visaInfo.push(`Issue Date: ${dateHandler(employee.visa.issue_date)}`);
    if (employee.visa.expiration_date && employee.visa.expiration_date !== "NA") {
      visaInfo.push(`Expiry: ${dateHandler(employee.visa.expiration_date)}`);
    }

    const displayText = visaInfo.join(", ");

    return (
      <div className="d-flex align-items-center">
        <span>{displayText}</span>
        {hasAttachment && (
          <a
            href={employee.visa.attachment_url}
            target="_blank"
            rel="noopener noreferrer"
            download
            className="ms-2"
          >
            <i className="fas fa-download" />
          </a>
        )}
      </div>
    );
  };

  const getRtwSubmissionStatus = (employee) => {
    if (employee.is_british_employee) {
      return {
        status: "Not Required",
        variant: "info",
      };
    }

    if (!employee.rtw?.rtw_submit_date || employee.rtw.rtw_submit_date === "NA") {
      return {
        status: "Need to Upload",
        variant: "warning",
      };
    }

    if (!employee.start_date || employee.start_date === "NA") {
      return {
        status: "Start Date Missing",
        variant: "danger",
      };
    }

    const rtwSubmitDate = new Date(employee.rtw.rtw_submit_date);
    const joiningDate = new Date(employee.start_date);

    if (rtwSubmitDate < joiningDate) {
      return {
        status: "Before Joining",
        variant: "success",
      };
    } else if (rtwSubmitDate.toDateString() === joiningDate.toDateString()) {
      return {
        status: "On Joining Date",
        variant: "success",
      };
    } else {
      return {
        status: "After Joining",
        variant: "danger",
      };
    }
  };

  const getRtwStatus = (employee) => {
    if (employee.is_british_employee) {
      return "Not Required";
    }

    if (!employee.rtw) return "Need to Upload";

    const hasRefCode = employee.rtw.reference && employee.rtw.reference !== "NA";
    const hasSubmitDate = employee.rtw.rtw_submit_date && employee.rtw.rtw_submit_date != 'NA';
    const hasExpiryDate = employee.rtw.expiry_date && employee.rtw.expiry_date !== "NA";
    const hasAttachment = employee.rtw.attachment_url && employee.rtw.attachment_url !== "NA";

    if (!hasRefCode || !hasSubmitDate) {
      return "Need to Upload";
    }

    const expiryDate = new Date(employee.rtw.expiry_date);
    const today = new Date();
    const status = expiryDate >= today ? "Valid" : "Expired";

    return (
      <div className="d-flex align-items-center">
        <span>Ref: {employee.rtw.reference}</span>
        {/* <span className="ms-2">Submit Date: {dateHandler(employee.rtw.rtw_submit_date)}</span> */}
        {hasAttachment && (
          <a
            href={employee.rtw.attachment_url}
            target="_blank"
            rel="noopener noreferrer"
            download
            className="ms-2"
          >
            <i className="fas fa-download" />
          </a>
        )}
      </div>
    );
  };

  const getCosStatus = (employee) => {
    if (!employee.is_sponsored_employee) {
      return "Only For Sponsored";
    }

    if (!employee.cos) return "Need to Upload";

    const hasNumber = employee.cos.certificate_number && employee.cos.certificate_number !== "NA";
    const hasAssignedDate = employee.cos.assigned_date && employee.cos.assigned_date !== "NA";
    const hasExpiryDate = employee.cos.expiry_date && employee.cos.expiry_date !== "NA";
    const hasAttachment = employee.cos.attachment_url && employee.cos.attachment_url !== "NA";

    if (!hasNumber || !hasAssignedDate || !hasExpiryDate) {
      return "Need to Upload";
    }

    return (
      <div className="d-flex align-items-center">
        <span>Number: {employee.cos.certificate_number}</span>
        <span className="ms-2">Assigned: {dateHandler(employee.cos.assigned_date)}</span>
        <span className="ms-2">Expiry: {dateHandler(employee.cos.expiry_date)}</span>
        {hasAttachment && (
          <a
            href={employee.cos.attachment_url}
            target="_blank"
            rel="noopener noreferrer"
            download
            className="ms-2"
          >
            <i className="fas fa-download" />
          </a>
        )}
      </div>
    );
  };

  const getJobContractStatus = (employee) => {
    if (!employee.job_contract_url || employee.job_contract_url === "NA") {
      return "Need to Upload";
    }

    return (
      <a
        href={employee.job_contract_url}
        target="_blank"
        rel="noopener noreferrer"
        download
      >
        <i className="fas fa-download" />
      </a>
    );
  };

  const getDocumentsStatus = (employee) => {
    if (!employee.documents || employee.documents.length === 0) {
      return "No Documents";
    }

    return (
      <div>
        {employee.documents.map((doc, index) => (
          <div key={index} className="mb-1">
            <span>{doc.name || "Document"}</span>
            {doc.attachment_url && doc.attachment_url !== "NA" && (
              <a
                href={doc.attachment_url}
                target="_blank"
                rel="noopener noreferrer"
                download
                className="ms-2"
              >
                <i className="fas fa-download" />
              </a>
            )}
          </div>
        ))}
      </div>
    );
  };

  const getTimesheetStatus = (employee) => {
    if (!employee.latest_timesheet_date || employee.latest_timesheet_date === "NA") {
      return "Need to Update";
    }
    return dateHandler(employee.latest_timesheet_date);
  };

  const getPayslipStatus = (employee) => {
    if (!employee.latest_payslip_date || employee.latest_payslip_date === "NA") {
      return "Need to Update";
    }

    return (
      <div className="d-flex align-items-center">
        <span>{dateHandler(employee.latest_payslip_date)}</span>
        {/* {employee.latest_payslip_date !== "NA" && (
          <a
            href={employee.latest_payslip_date}
            target="_blank"
            rel="noopener noreferrer"
            download
            className="ms-2"
          >
            <i className="fas fa-download" />
          </a>
        )} */}
      </div>
    );
  };

  const getEmergencyContactStatus = (employee) => {
    const hasWorkPhone = employee.emergency_contact_info.emergency_contact_number && employee.emergency_contact_info.emergency_contact_number !== "NA";
    const hasWorkEmail = employee.emergency_contact_info.emergency_contact_email && employee.emergency_contact_info.emergency_contact_email !== "NA";

    if (!hasWorkPhone && !hasWorkEmail) {
      return "Need to Update";
    }

    return (
      <div>
        {hasWorkEmail && <div>{employee.emergency_contact_info.emergency_contact_number}</div>}
        {hasWorkPhone && <div>{employee.emergency_contact_info.emergency_contact_email}</div>}
      </div>
    );
  };

  // Helper function to determine cell styling based on status
  const getCellStyle = (value, isBadge = false) => {
    let className = "";

    if (typeof value === "string") {
      if (
        value.includes("Need to") ||
        value.includes("Missing") ||
        value === "No Documents" ||
        value === "Not Assigned" ||
        value === "Expired" ||
        value === "After Joining"
      ) {
        className = "table-warning text-white";
      } else if (
        value === "Valid" ||
        value === "Before Joining" ||
        value === "On Joining Date" ||
        value === "Updated" ||
        value === "Yes"
      ) {
        className = "table-success text-white";
      } else if (
        value === "Not Required" ||
        value === "Only For Sponsored"
      ) {
        className = "table-info text-white";
      }
    } else if (!isBadge) {
      // For non-string values (components), consider them as valid
      className = "table-success text-black";
    }

    return className;
  };


  // Updated getLeaveStatus with taken leave check
  const getLeaveStatus = (employee) => {
    if (!employee.leave_balances || employee.leave_balances.length === 0) {
      return {
        status: "Not Assigned",
        hasTakenLeave: false
      };
    }

    const currentYear = new Date().getFullYear();
    const currentLeave = employee.leave_balances.find(
      (leave) => leave.year === currentYear
    );

    if (!currentLeave) {
      return {
        status: "Not Assigned",
        hasTakenLeave: false
      };
    }

    const totalLeaves = currentLeave.total_annual_days || 0;
    const takenLeaves = currentLeave.total_taken || 0;
    const remainingLeaves = totalLeaves - takenLeaves;

    return {
      status: `${takenLeaves} taken / ${remainingLeaves} remaining`,
      hasTakenLeave: takenLeaves > 0
    };
  };

  // Update transformData to use the new getLeaveStatus
  const transformData = (apiData) => {
    if (!apiData) return [];

    return apiData.map((employee) => {
      const rtwSubmission = getRtwSubmissionStatus(employee);
      const leaveStatus = getLeaveStatus(employee);

      return {
        profile: (
          <div className="d-flex align-items-center">
            {getAvatarContent(employee)}
            <div className="ms-2">
              <Link to={`/employee-profile-view/${employee?.employee_uuid}`}>
              <div>{employee.employee_fullname || "Need to Update"}</div>
              </Link>
              <div className="small text-muted">
                {employee.work_email || "Need to Update"}
              </div>
            </div>
          </div>
        ),
        is_british: employee.is_british_employee ? "Yes" : "No",
        is_sponsored: employee.is_sponsored_employee ? "Yes" : "No",
        passport: getPassportStatus(employee),
        visa: getVisaStatus(employee),
        rtw_submission: {
          ...rtwSubmission,
          date: employee.rtw?.rtw_submit_date || "NA",
        },
        rtw: getRtwStatus(employee),
        cos: getCosStatus(employee),
        job_contract: getJobContractStatus(employee),
        documents: getDocumentsStatus(employee),
        timesheet: getTimesheetStatus(employee),
        payslip: getPayslipStatus(employee),
        emergency_contact: getEmergencyContactStatus(employee),
        leaves: leaveStatus,
        rawData: employee,
      };
    });
  };



  useEffect(() => {
    if (complianceData) {
      const transformedData = transformData(complianceData);
      setOriginalData(transformedData);
      setDisplayData(transformedData);
    }
  }, [complianceData]);

  const requestSort = (key) => {
    let direction = "asc";
    if (sortConfig.key === key && sortConfig.direction === "asc") {
      direction = "desc";
    }
    setSortConfig({ key, direction });

    const sortedData = [...displayData].sort((a, b) => {
      const valueA = key.includes(".")
        ? key.split(".").reduce((o, i) => o[i], a)
        : a[key];
      const valueB = key.includes(".")
        ? key.split(".").reduce((o, i) => o[i], b)
        : b[key];

      if (valueA < valueB) return direction === "asc" ? -1 : 1;
      if (valueA > valueB) return direction === "asc" ? 1 : -1;
      return 0;
    });
    setDisplayData(sortedData);
  };

  useEffect(() => {
    let filteredData = [...originalData];

    if (searchTerm) {
      filteredData = filteredData?.filter((item) =>
        Object.values(item).some((val) => {
          if (typeof val === "object" && val !== null) {
            return Object.values(val).some((subVal) =>
              subVal?.toString()?.toLowerCase()?.includes(searchTerm?.toLowerCase())
            );
          }
          return val
            .toString()
            .toLowerCase()
            .includes(searchTerm?.toLowerCase());
        })
      );
    }

    Object.entries(filters).forEach(([key, value]) => {
      if (value) {
        filteredData = filteredData.filter((item) => {
          const itemValue = item[key];
          if (typeof itemValue === "object") {
            return (
              itemValue.status.toString().toLowerCase() === value.toLowerCase()
            );
          }
          return itemValue.toString().toLowerCase() === value.toLowerCase();
        });
      }
    });

    setDisplayData(filteredData);
    setCurrentPage(1);
  }, [searchTerm, filters, originalData]);

  const exportToExcel = () => {
    const dataForExport = displayData.map((item) => ({
     employee_fullname: item?.rawData?.employee_fullname || "NA",
      ...item,
      passport_expiry: item.rawData.passport?.expiration_date || "NA",
      visa_expiry: item.rawData.visa?.expiration_date || "NA",
      rtw_expiry: item.rawData.rtw?.expiry_date || "NA",
      joining_date: item.rawData.start_date || "NA",
      rtw_submission_date: item.rawData.rtw?.rtw_submit_date || "NA",
      cos_assigned_date: item.rawData.cos?.assigned_date || "NA",
      cos_expiry_date: item.rawData.cos?.expiry_date || "NA",
    }));


    const ws = XLSX.utils.json_to_sheet(dataForExport);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "ComplianceData");
    XLSX.writeFile(wb, "compliance_check.xlsx");
  };

  // Updated StatusBadge component with border styling
  const StatusBadge = ({ status, variant, hasBorder = true }) => {
    if (variant) {
      return (
        <Badge
          bg={variant}
          className={hasBorder ? getCellStyle(status, true) : ""}
        >
          {status}
        </Badge>
      );
    }

    let badgeVariant = "secondary";
    if (
      status === "Valid" ||
      status === "Yes" ||
      status === "Before Joining" ||
      status === "On Joining Date" ||
      (typeof status !== "string" && status !== "Need to Upload")
    ) {
      badgeVariant = "success";
    }
    if (
      status === "Expired" ||
      status === "No" ||
      status === "After Joining" ||
      status === "Need to Upload" ||
      status === "Need to Update" ||
      status === "Start Date Missing"
    ) {
      badgeVariant = "danger";
    }
    if (
      status === "NA" ||
      status === "Not Assigned" ||
      status === "No Documents"
    ) {
      badgeVariant = "warning";
    }
    if (status === "Not Required" || status === "Only For Sponsored") {
      badgeVariant = "info";
    }

    return (
      <Badge
        bg={badgeVariant}
        className={hasBorder ? getCellStyle(status, true) : ""}
      >
        {status}
      </Badge>
    );
  };


  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = displayData.slice(indexOfFirstItem, indexOfLastItem);
  const totalPages = Math.ceil(displayData.length / itemsPerPage);

  return (
    <Fragment>
      <div className="row">
        <Col lg={12}>
          <Card>
            <Card.Header className="d-flex justify-content-between align-items-center">
              <Card.Title>Employee Compliance Check</Card.Title>
              <div>
                <Button variant="primary" onClick={exportToExcel}>
                  Export to Excel
                </Button>
              </div>
            </Card.Header>
            <Card.Body>
              {isLoading ? (
                <div className="text-center py-5">
                  <Spinner animation="border" variant="primary" size="lg" />
                  <div className="mt-3">Loading compliance data...</div>
                </div>
              ) : (
                <>
                  <div className="mb-4">
                    <Form.Group className="mb-3">
                      <Form.Control
                        type="text"
                        placeholder="Search"
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                      />
                    </Form.Group>

                    <div className="row g-3">
                      <Col md={3}>
                        <Form.Select
                          value={filters.is_british}
                          onChange={(e) =>
                            setFilters({ ...filters, is_british: e.target.value })
                          }
                        >
                          <option value="">All Employee Status</option>
                          <option value="Yes">British</option>
                          <option value="No">Non-British</option>
                        </Form.Select>
                      </Col>
                      <Col md={3}>
                        <Form.Select
                          value={filters.is_sponsored}
                          onChange={(e) =>
                            setFilters({ ...filters, is_sponsored: e.target.value })
                          }
                        >
                          <option value="">All Employee</option>
                          <option value="Yes">Sponsored</option>
                          <option value="No">Not Sponsored</option>
                        </Form.Select>
                      </Col>
                    </div>
                  </div>

                  <Table responsive striped bordered hover style={{ tableLayout: 'auto', width: '100%' }}>
                    <thead>
                      <tr>
                        <th>Profile</th>
                        <th onClick={() => requestSort("is_british")}>
                          Is British{" "}
                          {sortConfig.key === "is_british" &&
                            (sortConfig.direction === "asc" ? "↑" : "↓")}
                        </th>
                        <th onClick={() => requestSort("is_sponsored")}>
                          Is Sponsored{" "}
                          {sortConfig.key === "is_sponsored" &&
                            (sortConfig.direction === "asc" ? "↑" : "↓")}
                        </th>
                        <th>Passport</th>
                        <th>Visa</th>
                        <th onClick={() => requestSort("rtw_submission.status")}>
                          RTW Submission{" "}
                          {sortConfig.key === "rtw_submission.status" &&
                            (sortConfig.direction === "asc" ? "↑" : "↓")}
                        </th>
                        <th>RTW</th>
                        <th>COS</th>
                        <th>Job Contract</th>
                        <th>Documents</th>
                        <th>Timesheet(Lastest Entry)</th>
                        <th>Payslip(Lastest Entry)</th>
                        <th>Emergency Contact</th>
                        <th>Leaves</th>
                      </tr>
                    </thead>
                    <tbody>
                      {currentItems?.length > 0 ? (
                        currentItems?.map((item, index) => (
                          <tr key={index}>
                            <td>{item.profile}</td>
                            <td>
                              <StatusBadge status={item.is_british} />
                            </td>
                            <td>
                              <StatusBadge status={item.is_sponsored} />
                            </td>
                            <td style={{width:'auto'}} className={getCellStyle(item.passport)}>
                              {typeof item.passport === "string" ? (
                                <StatusBadge status={item.passport} />
                              ) : (
                                item.passport
                              )}
                            </td>
                            <td style={{width:'auto'}} className={getCellStyle(item.visa)}>
                              {typeof item.visa === "string" ? (
                                <StatusBadge status={item.visa} />
                              ) : (
                                item.visa
                              )}
                            </td>
                            <td className={getCellStyle(item.rtw_submission.status)}>
                              <StatusBadge
                                status={item.rtw_submission.status}
                                variant={item.rtw_submission.variant}
                              />
                              {item.rtw_submission.date !== "NA" && (
                                <div className="small text-muted mt-1">
                                  {dateHandler(item.rtw_submission.date)}
                                </div>
                              )}
                            </td>
                            <td className={getCellStyle(item.rtw)}>
                              {typeof item.rtw === "string" ? (
                                <StatusBadge status={item.rtw} />
                              ) : (
                                item.rtw
                              )}
                            </td>
                            <td className={getCellStyle(item.cos)}>
                              {typeof item.cos === "string" ? (
                                <StatusBadge status={item.cos} />
                              ) : (
                                item.cos
                              )}
                            </td>
                            <td className={getCellStyle(item.job_contract)}>
                              {typeof item.job_contract === "string" ? (
                                <StatusBadge status={item.job_contract} />
                              ) : (
                                item.job_contract
                              )}
                            </td>
                            <td className={getCellStyle(item.documents)}>
                              {typeof item.documents === "string" ? (
                                <StatusBadge status={item.documents} />
                              ) : (
                                item.documents
                              )}
                            </td>
                            <td className={getCellStyle(item.timesheet)}>
                              <StatusBadge status={item.timesheet} />
                            </td>
                            <td className={getCellStyle(item.payslip)}>
                              {typeof item.payslip === "string" ? (
                                <StatusBadge status={item.payslip} />
                              ) : (
                                item.payslip
                              )}
                            </td>
                            <td className={getCellStyle(item.emergency_contact)}>
                              {typeof item.emergency_contact === "string" ? (
                                <StatusBadge status={item.emergency_contact} />
                              ) : (
                                item.emergency_contact
                              )}
                            </td>
                            <td className={item.leaves.hasTakenLeave ? "border-success" : getCellStyle(item.leaves.status)}>
                              <StatusBadge
                                status={item.leaves.status}
                                variant={item.leaves.hasTakenLeave ? "success" : undefined}
                                hasBorder={false}
                              />
                            </td>
                          </tr>
                        ))
                      ) : (
                        <tr>
                          <td colSpan={14} style={{ textAlign: "center" }}>
                            {complianceData?.length === 0
                              ? "No compliance data available"
                              : "No records found matching your criteria"}
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </Table>

                  {displayData?.length > itemsPerPage && (
                    <div className="d-flex justify-content-center mt-3">
                      <Pagination>
                        <Pagination.First
                          onClick={() => setCurrentPage(1)}
                          disabled={currentPage === 1}
                        />
                        <Pagination.Prev
                          onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                          disabled={currentPage === 1}
                        />

                        {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                          let pageNum;
                          if (totalPages <= 5) {
                            pageNum = i + 1;
                          } else if (currentPage <= 3) {
                            pageNum = i + 1;
                          } else if (currentPage >= totalPages - 2) {
                            pageNum = totalPages - 4 + i;
                          } else {
                            pageNum = currentPage - 2 + i;
                          }

                          return (
                            <Pagination.Item
                              key={pageNum}
                              active={pageNum === currentPage}
                              onClick={() => setCurrentPage(pageNum)}
                            >
                              {pageNum}
                            </Pagination.Item>
                          );
                        })}

                        <Pagination.Next
                          onClick={() =>
                            setCurrentPage((p) => Math.min(totalPages, p + 1))
                          }
                          disabled={currentPage === totalPages}
                        />
                        <Pagination.Last
                          onClick={() => setCurrentPage(totalPages)}
                          disabled={currentPage === totalPages}
                        />
                      </Pagination>
                    </div>
                  )}
                </>
              )}
            </Card.Body>
          </Card>
        </Col>
      </div>
    </Fragment>
  );
}

export default ComplianceCheck;